import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../Gallery.css';
import { numberToWords } from '../../utils/helpers';
import ThingThumbnail from './ThingThumbnail';
import { useGlobalContext } from '../../utils/GlobalContext';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { getIdsForTrait } from '../../utils/thingsData';

const HOVER_COLORS = ['#ffc964', '#d65952', '#7ec28a', '#6ba1cf', '#a089f1'];

export default function Things() {
  const TOTAL_IMAGES = 10000;
  const INITIAL_LOAD = 16;
  const LOAD_MORE = 16;
  const { numbersActive, imagesActive, tokenSearchVal, setTokenSearchVal } = useGlobalContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const traitType = searchParams.get('trait_type');
  const traitValue = searchParams.get('trait_value');
  const INITIAL_NUMBERS_LOAD = 1000;
  const LOAD_MORE_NUMBERS = 1000;
  const [loadedImagesCount, setLoadedImagesCount] = useState(INITIAL_LOAD);
  const [loadedNumbersCount, setLoadedNumbersCount] = useState(INITIAL_NUMBERS_LOAD);

  // Trait filter is now backed by the inverted index in things-data.json,
  // fetched lazily only when the URL has trait_type + trait_value params.
  const [traitFilteredIdsRaw, setTraitFilteredIdsRaw] = useState(undefined);
  useEffect(() => {
    if (!traitType || !traitValue) {
      setTraitFilteredIdsRaw(undefined);
      return;
    }
    let cancelled = false;
    setTraitFilteredIdsRaw(null);
    getIdsForTrait(traitType, traitValue)
      .then((ids) => { if (!cancelled) setTraitFilteredIdsRaw(ids); })
      .catch((err) => {
        console.warn('trait filter load failed', err);
        if (!cancelled) setTraitFilteredIdsRaw([]);
      });
    return () => { cancelled = true; };
  }, [traitType, traitValue]);

  // Gate the filtered IDs synchronously on the URL params. Without this gate,
  // there's a render after navigating away from a trait page where the URL
  // has cleared but the async setState hasn't fired yet — and downstream
  // code calls traitValue.toLowerCase() in that window, crashing the page.
  const traitFilteredIds = (traitType && traitValue) ? traitFilteredIdsRaw : undefined;

  const loadMoreImages = useCallback(() => {
    setLoadedImagesCount(prev => {
      const nextCount = prev + LOAD_MORE;
      return Math.min(nextCount, TOTAL_IMAGES);
    });
  }, []);

  const filteredImages = useMemo(() => {
    if (!tokenSearchVal) return undefined;
    if (+tokenSearchVal < 1 || +tokenSearchVal > TOTAL_IMAGES) return undefined;
    if (!Number.isInteger(+tokenSearchVal)) return undefined;
    return [+tokenSearchVal];
  }, [tokenSearchVal]);

  useEffect(() => {
    if (tokenSearchVal) {
      setTokenSearchVal('');
    }
  }, []);

  const loadMoreNumbers = useCallback(() => {
    setLoadedNumbersCount(prev => Math.min(prev + LOAD_MORE_NUMBERS, TOTAL_IMAGES));
  }, []);

  useEffect(() => {
    if (!!filteredImages || !!traitFilteredIds) return;
    if (!imagesActive && !numbersActive) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 200) {
        if (imagesActive && loadedImagesCount < TOTAL_IMAGES) {
          loadMoreImages();
        }
        if (numbersActive && loadedNumbersCount < TOTAL_IMAGES) {
          loadMoreNumbers();
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [imagesActive, numbersActive, loadedImagesCount, loadedNumbersCount, loadMoreImages, loadMoreNumbers, filteredImages, traitFilteredIds]);


  const traitLinkState = traitFilteredIds ? { fromTrait: { traitType, traitValue } } : undefined;

  return (
    <div>
      <div className="home-container">
        <div className="body-text">
          {traitFilteredIds
            ? <>There {traitFilteredIds.length === 1 ? 'is' : 'are'} {traitFilteredIds.length} {traitFilteredIds.length === 1 ? 'Thing' : 'Things'} with the <strong>{traitValue.toLowerCase()}</strong> {traitType.toLowerCase()} trait.</>
            : <>&nbsp;</>}
        </div>

        {imagesActive && (
          <div className="grid-container">
            {(filteredImages || traitFilteredIds || []).length > 0
              ? (filteredImages || traitFilteredIds).map(image => (
                <div key={`thing-${image}`} className="grid-item">
                  <ThingThumbnail imageIndex={image} linkState={traitLinkState} />
                </div>
              ))
              : !filteredImages && !traitFilteredIds && Array.from({ length: loadedImagesCount }, (_, index) => (
                <div key={`thing-${index + 1}`} className="grid-item">
                  <ThingThumbnail imageIndex={index + 1} />
                </div>
              ))}
          </div>
        )}
        {numbersActive && (
          <div className={(filteredImages || traitFilteredIds) ? 'numbers-container-filtered' : 'numbers-container'}>
            {(filteredImages || traitFilteredIds) ? (filteredImages || traitFilteredIds).map((image, idx, arr) => (
              <React.Fragment key={`thing-${image}`}>
                <Link className="number-item" to={`/things/${image}`} state={traitLinkState} style={{ '--hover-color': HOVER_COLORS[image % HOVER_COLORS.length] }}>
                  {numberToWords(image)}
                </Link>
                {idx < arr.length - 1 && ', '}
              </React.Fragment>
            )) : Array.from({ length: loadedNumbersCount }, (_, index) => (
              <React.Fragment key={`thing-${index + 1}`}>
                <Link className="number-item" to={`/things/${index + 1}`} style={{ '--hover-color': HOVER_COLORS[index % HOVER_COLORS.length] }}>
                  {numberToWords(index + 1)}
                </Link>
                {index < loadedNumbersCount - 1 && ', '}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
